import { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface DropdownItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'primary' | 'success';
  disabled?: boolean;
  separator?: boolean; // Ajoute un séparateur après cet item
}

interface DropdownProps {
  items: DropdownItem[];
  trigger?: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

export function Dropdown({ items, trigger, align = 'right', className = '' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const getItemClasses = (variant?: string, disabled?: boolean) => {
    if (disabled) {
      return 'text-gray-400 cursor-not-allowed opacity-50';
    }
    
    switch (variant) {
      case 'danger':
        return 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20';
      case 'primary':
        return 'text-primary hover:bg-primary/10';
      case 'success':
        return 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20';
      default:
        return 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700';
    }
  };

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className}`}>
      {/* Trigger button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
        aria-label="Actions"
        aria-expanded={isOpen}
      >
        {trigger || <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-400" />}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`
            absolute z-50 mt-2 w-48 rounded-lg shadow-lg
            bg-white dark:bg-gray-800 
            border border-gray-200 dark:border-gray-700
            py-1
            ${align === 'right' ? 'right-0' : 'left-0'}
          `}
        >
          {items.map((item, index) => (
            <div key={index}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemClick(item);
                }}
                disabled={item.disabled}
                className={`
                  w-full flex items-center px-4 py-2.5 text-sm
                  transition-colors
                  ${getItemClasses(item.variant, item.disabled)}
                  ${item.disabled ? '' : 'cursor-pointer'}
                `}
              >
                {item.icon && (
                  <item.icon className="h-4 w-4 mr-3" />
                )}
                <span>{item.label}</span>
              </button>
              {item.separator && index < items.length - 1 && (
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
